// frontend/src/components/formularios/campos/DashboardEstadisticoNeuro.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Grid, Card, CardContent, Chip, Divider,
  LinearProgress, Alert, Stack
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
         BarChart, Bar, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const DashboardEstadisticoNeuro = ({ data, objetivos = [], periodo }) => {
  const [estadisticas, setEstadisticas] = useState({});
  const [evolucionTemporal, setEvolucionTemporal] = useState([]);
  const [resumenGeneral, setResumenGeneral] = useState({});

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      calcularEstadisticas();
    }
  }, [data, objetivos]);

  const calcularEstadisticas = () => {
    const stats = {};
    const evolucion = [];
    let totalLogrados = 0;
    let totalEnProceso = 0;
    let totalReprogramacion = 0;

    // Procesar cada objetivo
    Object.keys(data).forEach(objId => {
      const evaluacion = data[objId];
      if (!evaluacion) return;

      const obj = objetivos.find(o => o.id === objId);
      if (!obj) return;

      // Calcular promedio y tendencia
      const puntajes = [];
      if (evaluacion.ses1_puntaje) puntajes.push(parseFloat(evaluacion.ses1_puntaje));
      if (evaluacion.ses2_puntaje) puntajes.push(parseFloat(evaluacion.ses2_puntaje));

      const promedio = puntajes.length > 0 ? 
        puntajes.reduce((a, b) => a + b, 0) / puntajes.length : 0;

      let estado = 'Sin evaluar';
      let color = 'default';
      if (promedio >= 3) {
        estado = 'Logrado';
        color = 'success';
        totalLogrados++;
      } else if (promedio >= 2) {
        estado = 'En Proceso';
        color = 'warning';
        totalEnProceso++;
      } else if (promedio > 0) {
        estado = 'Reprogramación';
        color = 'error';
        totalReprogramacion++;
      }

      // Calcular tendencia
      let tendencia = 'estable';
      if (puntajes.length >= 2) {
        const diferencia = puntajes[puntajes.length - 1] - puntajes[0];
        tendencia = diferencia > 0.5 ? 'mejora' : 
                   diferencia < -0.5 ? 'declive' : 'estable';
      }

      stats[objId] = {
        objetivo: obj,
        puntajes,
        promedio,
        estado,
        color,
        tendencia,
        ultimoPuntaje: puntajes[puntajes.length - 1] || 0,
        fechas: [evaluacion.ses1_fecha, evaluacion.ses2_fecha].filter(f => f)
      };

      // Datos para gráfico temporal
      puntajes.forEach((puntaje, idx) => {
        evolucion.push({
          objetivo: `Obj. ${obj.objetivo_numero || objId}`,
          sesion: `Sesión ${idx + 1}`,
          puntaje,
          fecha: idx === 0 ? evaluacion.ses1_fecha : evaluacion.ses2_fecha
        });
      });
    });

    setEstadisticas(stats);
    setEvolucionTemporal(evolucion);
    setResumenGeneral({
      total: Object.keys(stats).length,
      logrados: totalLogrados,
      enProceso: totalEnProceso,
      reprogramacion: totalReprogramacion,
      porcentajeLogro: Object.keys(stats).length > 0 ? 
        Math.round((totalLogrados / Object.keys(stats).length) * 100) : 0
    });
  };

  const getTendenciaIcon = (tendencia) => {
    switch (tendencia) {
      case 'mejora': return <TrendingUpIcon color="success" />;
      case 'declive': return <TrendingDownIcon color="error" />;
      default: return <TrendingFlatIcon color="action" />;
    }
  };

  const colorPorPuntaje = ['#f44336', '#ff9800', '#4caf50']; // Rojo, Naranja, Verde

  if (!data || Object.keys(data).length === 0) {
    return (
      <Alert severity="info">
        No hay datos de evaluación para mostrar estadísticas
      </Alert>
    );
  }

  return (
    <Box>
      {/* Resumen General */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {resumenGeneral.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Objetivos Evaluados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {resumenGeneral.logrados}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Logrados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {resumenGeneral.enProceso}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                En Proceso
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {resumenGeneral.porcentajeLogro}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Porcentaje de Logro
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={resumenGeneral.porcentajeLogro} 
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Tabla Estadística (Modelo PDF) */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <AssessmentIcon sx={{ mr: 1 }} />
              Resumen Estadístico por Objetivo
            </Typography>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>S.</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ses.1</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ses.2</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Prom.</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tend.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(estadisticas).map(([objId, stat]) => (
                    <TableRow key={objId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {stat.objetivo.objetivo_numero || objId.slice(-2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={stat.puntajes[0] || '-'} 
                          size="small"
                          color={stat.puntajes[0] >= 3 ? 'success' : 
                                 stat.puntajes[0] >= 2 ? 'warning' : 'error'}
                          variant={stat.puntajes[0] ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={stat.puntajes[1] || '-'} 
                          size="small"
                          color={stat.puntajes[1] >= 3 ? 'success' : 
                                 stat.puntajes[1] >= 2 ? 'warning' : 'error'}
                          variant={stat.puntajes[1] ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          {stat.promedio > 0 ? stat.promedio.toFixed(1) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={stat.estado}
                          size="small"
                          color={stat.color}
                          icon={stat.estado === 'Logrado' ? <CheckCircleIcon /> : 
                                stat.estado === 'En Proceso' ? <WarningIcon /> : undefined}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {getTendenciaIcon(stat.tendencia)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Gráfico de Evolución */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Evolución por Sesiones
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={evolucionTemporal}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="sesion" />
                <YAxis domain={[0, 3]} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="puntaje" 
                  stroke="#2196f3" 
                  strokeWidth={2}
                  dot={{ fill: '#2196f3' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Distribución de Puntajes */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Distribución de Estados
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Logrado', value: resumenGeneral.logrados, color: '#4caf50' },
                    { name: 'En Proceso', value: resumenGeneral.enProceso, color: '#ff9800' },
                    { name: 'Reprogramación', value: resumenGeneral.reprogramacion, color: '#f44336' }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  dataKey="value"
                >
                  {[
                    { name: 'Logrado', value: resumenGeneral.logrados, color: '#4caf50' },
                    { name: 'En Proceso', value: resumenGeneral.enProceso, color: '#ff9800' },
                    { name: 'Reprogramación', value: resumenGeneral.reprogramacion, color: '#f44336' }
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Leyenda de Puntajes */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Leyenda de Evaluación
            </Typography>
            <Stack spacing={1}>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip label="3" color="success" size="small" />
                <Typography variant="body2">
                  Lo hace de manera independiente
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip label="2" color="warning" size="small" />
                <Typography variant="body2">
                  Al modelo/apoyo visual
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip label="1" color="error" size="small" />
                <Typography variant="body2">
                  No lo hace
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="textSecondary">
                <strong>Códigos adicionales:</strong> NT (No se trabajó), NA (Faltó/No asistió), O (Otros)
              </Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardEstadisticoNeuro;